import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Slider } from "./ui/slider";
import {
  Play,
  Pause,
  Check,
  Mic,
  Brain,
  User as UserIcon,
  Loader2,
  Upload,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { auth } from "../lib/api";

const AvatarUploader = ({ user, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const avatars = user?.avatars || [];

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8_000_000) {
      toast.error("Image too large (8 MB max)");
      return;
    }
    if (avatars.length >= 10) {
      toast.error("Max 10 avatars — remove one first");
      return;
    }
    setBusy(true);
    try {
      const res = await auth.uploadAvatar(f, label.trim() || undefined);
      onChange?.({
        ...user,
        avatars: res.avatars,
        avatar_url: res.avatars[0]?.url || null,
      });
      setLabel("");
      toast.success(`Added "${res.avatar.label}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id) => {
    setBusy(true);
    try {
      const res = await auth.deleteAvatarById(id);
      onChange?.({
        ...user,
        avatars: res.avatars,
        avatar_url: res.avatars[0]?.url || null,
      });
      toast.success("Removed");
    } catch (err) {
      toast.error("Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const renameOne = async (id, current) => {
    const next = window.prompt("Rename avatar", current);
    if (!next || next.trim() === current) return;
    try {
      const res = await auth.renameAvatar(id, next.trim());
      onChange?.({ ...user, avatars: res.avatars });
    } catch (err) {
      toast.error("Rename failed");
    }
  };

  return (
    <div className="space-y-3">
      {avatars.length > 0 && (
        <div
          data-testid="avatar-gallery"
          className="grid grid-cols-3 sm:grid-cols-4 gap-3"
        >
          {avatars.map((a) => (
            <div
              key={a.id}
              data-testid={`avatar-card-${a.id}`}
              className="group relative rounded-xl border border-white/10 bg-black/40 overflow-hidden"
            >
              <img
                src={a.url}
                alt={a.label}
                className="w-full aspect-square object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-1.5 py-1 text-[10px] leading-tight text-white/90 truncate">
                {a.label}
              </div>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 flex items-center justify-center gap-1">
                <button
                  onClick={() => renameOne(a.id, a.label)}
                  className="text-[10px] px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  Rename
                </button>
                <button
                  data-testid={`avatar-remove-${a.id}`}
                  onClick={() => removeOne(a.id)}
                  disabled={busy}
                  className="text-[10px] px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/40 text-red-200"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {avatars.length === 0 && (
        <div className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10 rounded-lg">
          No avatars yet. Upload a photo of yourself (or anyone) below.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          data-testid="avatar-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Me, Superhero, 90s look)"
          className="flex-1 min-w-[160px] bg-black/40 border-white/10"
          maxLength={40}
        />
        <label
          data-testid="avatar-upload-btn"
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 cursor-pointer text-sm ${
            busy || avatars.length >= 10
              ? "opacity-50 pointer-events-none"
              : ""
          }`}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Add photo
          <input
            type="file"
            accept="image/*"
            onChange={onPick}
            className="hidden"
            disabled={busy || avatars.length >= 10}
          />
        </label>
      </div>
      <div className="text-[11px] text-white/40">
        PNG · JPG · WEBP · max 8 MB · up to 10 avatars.
      </div>
    </div>
  );
};

export const SettingsDialog = ({ open, onOpenChange, user, onSaved }) => {
  const [tab, setTab] = useState("assistant");
  const [assistantName, setAssistantName] = useState(user?.assistant_name || "Glitch");
  const [voiceId, setVoiceId] = useState(user?.voice_id || "");
  const [speakingSpeed, setSpeakingSpeed] = useState(
    typeof user?.speaking_speed === "number" ? user.speaking_speed : 1.1
  );
  const [memory, setMemory] = useState(user?.memory || "");
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const audioRef = React.useRef(null);

  // Only re-seed form state (and reset the tab) on the false → true open
  // transition. Otherwise a mid-dialog user-object refresh (e.g. avatar
  // upload → onSaved → setUser in Chat.jsx) would kick the user back to
  // the Assistant tab and blow away in-flight edits.
  const prevOpen = React.useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setAssistantName(user?.assistant_name || "Glitch");
      setVoiceId(user?.voice_id || "");
      setSpeakingSpeed(
        typeof user?.speaking_speed === "number" ? user.speaking_speed : 1.1
      );
      setMemory(user?.memory || "");
      setTab("assistant");
    }
    prevOpen.current = open;
  }, [open, user]);

  useEffect(() => {
    if (!open || voices.length > 0) return;
    setLoadingVoices(true);
    auth
      .listVoices()
      .then((r) => setVoices(r.voices || []))
      .catch((e) => {
        toast.error(
          e?.response?.data?.detail || "Couldn't load voices"
        );
      })
      .finally(() => setLoadingVoices(false));
  }, [open]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const playPreview = (v) => {
    if (!v.preview_url) return;
    if (previewing === v.voice_id) {
      audioRef.current?.pause();
      setPreviewing(null);
      return;
    }
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.pause();
    audioRef.current.src = v.preview_url;
    audioRef.current.onended = () => setPreviewing(null);
    audioRef.current.play().catch(() => {});
    setPreviewing(v.voice_id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (assistantName.trim() !== (user?.assistant_name || "").trim()) {
        payload.assistant_name = assistantName.trim();
      }
      if (voiceId && voiceId !== (user?.voice_id || "")) {
        payload.voice_id = voiceId;
      }
      const currentSpeed =
        typeof user?.speaking_speed === "number" ? user.speaking_speed : 1.1;
      if (Math.abs(speakingSpeed - currentSpeed) > 0.001) {
        payload.speaking_speed = Number(speakingSpeed.toFixed(2));
      }
      let updatedUser = user;
      if (Object.keys(payload).length > 0) {
        const res = await auth.updatePreferences(payload);
        updatedUser = res.user;
      }
      if (memory !== (user?.memory || "")) {
        await auth.updateMemory(memory);
        updatedUser = { ...updatedUser, memory };
      }
      onSaved?.(updatedUser);
      toast.success("Saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const currentVoiceName = useMemo(() => {
    const v = voices.find((v) => v.voice_id === voiceId);
    return v?.name || "Default";
  }, [voices, voiceId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-dialog"
        className="glass-strong border-white/10 max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-blue-300" />
            Your settings
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Name your assistant, pick a voice, and tune the memory it keeps.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList
            data-testid="settings-tabs"
            className="bg-black/30 border border-white/10"
          >
            <TabsTrigger
              value="assistant"
              data-testid="settings-tab-assistant"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <UserIcon className="h-3.5 w-3.5 mr-1.5" /> Assistant
            </TabsTrigger>
            <TabsTrigger
              value="voice"
              data-testid="settings-tab-voice"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <Mic className="h-3.5 w-3.5 mr-1.5" /> Voice
            </TabsTrigger>
            <TabsTrigger
              value="memory"
              data-testid="settings-tab-memory"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <Brain className="h-3.5 w-3.5 mr-1.5" /> Memory
            </TabsTrigger>
            <TabsTrigger
              value="avatar"
              data-testid="settings-tab-avatar"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <UserIcon className="h-3.5 w-3.5 mr-1.5" /> Avatar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assistant" className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-white/60">
                Assistant name
              </Label>
              <Input
                data-testid="assistant-name-input"
                value={assistantName}
                onChange={(e) => setAssistantName(e.target.value)}
                maxLength={60}
                placeholder="e.g. Glitch, Ada, Nova, Copilot…"
                className="bg-black/40 border-white/10 h-11 focus-visible:ring-blue-500/40"
              />
              <div className="text-[11px] text-white/40">
                This is the name it introduces itself with and answers to.
              </div>
            </div>
          </TabsContent>

          <TabsContent value="voice" className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              Currently: {currentVoiceName}
            </div>

            <div
              data-testid="speaking-speed-section"
              className="mb-4 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3"
            >
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">
                  Speaking speed
                </Label>
                <span
                  data-testid="speaking-speed-value"
                  className="text-xs font-mono text-blue-300 tabular-nums"
                >
                  {speakingSpeed.toFixed(2)}×
                </span>
              </div>
              <Slider
                data-testid="speaking-speed-slider"
                value={[speakingSpeed]}
                min={0.7}
                max={1.2}
                step={0.05}
                onValueChange={(v) =>
                  setSpeakingSpeed(Array.isArray(v) ? v[0] : v)
                }
              />
              <div className="mt-2 flex items-center justify-between text-[10px] text-white/40">
                <span>Slower · 0.70×</span>
                <span>Default 1.10×</span>
                <span>Faster · 1.20×</span>
              </div>
              <div className="mt-2 text-[11px] text-white/45 leading-relaxed">
                Changes take effect on your next voice session.
              </div>
            </div>

            {loadingVoices && (
              <div className="text-sm text-white/50 py-6 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading voices…
              </div>
            )}
            {!loadingVoices && voices.length === 0 && (
              <div className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10 rounded-lg">
                No voices available. Confirm the ElevenLabs key on the backend.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[45vh] overflow-y-auto pr-1">
              {voices.map((v) => {
                const selected = voiceId === v.voice_id;
                const playing = previewing === v.voice_id;
                return (
                  <div
                    key={v.voice_id}
                    role="button"
                    tabIndex={0}
                    data-testid={`voice-option-${v.voice_id}`}
                    onClick={() => setVoiceId(v.voice_id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setVoiceId(v.voice_id);
                      }
                    }}
                    className={`text-left rounded-lg px-3 py-2.5 border transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${
                      selected
                        ? "border-blue-400/50 bg-blue-500/15"
                        : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {v.name}
                          {selected && (
                            <Check className="h-3.5 w-3.5 text-blue-300 shrink-0" />
                          )}
                        </div>
                        <div className="text-[11px] text-white/50 truncate">
                          {[v.gender, v.accent, v.description]
                            .filter(Boolean)
                            .join(" · ") || v.category || ""}
                        </div>
                      </div>
                      {v.preview_url && (
                        <button
                          type="button"
                          data-testid={`voice-preview-${v.voice_id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            playPreview(v);
                          }}
                          className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center shrink-0"
                          aria-label={playing ? "Stop preview" : "Play preview"}
                        >
                          {playing ? (
                            <Pause className="h-4 w-4 text-blue-300" />
                          ) : (
                            <Play className="h-4 w-4 text-white/70" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="memory" className="mt-4 space-y-2">
            <Label className="text-xs uppercase tracking-widest text-white/60">
              Baked-in memory
            </Label>
            <Textarea
              data-testid="memory-textarea"
              value={memory}
              onChange={(e) => setMemory(e.target.value)}
              rows={8}
              placeholder="Things you want the assistant to always know about you."
              className="bg-black/40 border-white/10 focus-visible:ring-blue-500/40 font-mono text-sm leading-relaxed"
            />
          </TabsContent>

          <TabsContent value="avatar" className="mt-4 space-y-3">
            <Label className="text-xs uppercase tracking-widest text-white/60">
              Your reference photo
            </Label>
            <div className="text-[11px] text-white/50 leading-relaxed">
              Upload a clear photo of your face. When you toggle
              <span className="text-white/80"> &quot;Use my avatar&quot;</span> in
              Studio, Glitch will use it as the reference face for images
              (PuLID Flux) and as the seed frame for videos (Kling I2V).
            </div>
            <AvatarUploader
              user={user}
              onChange={(u) => onSaved?.(u)}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            data-testid="settings-cancel-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
